import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faImage,
  faEdit,
  faSave,
  faUpload,
  faTrash,
  faEye,
  faPlus,
  faTimes,
  faExclamationTriangle,
  faCheck,
  faSpinner,
  faFileImage,
  faGlobe,
  faNewspaper,
  faUsers,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const ContentManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // 网站内容数据
  const [content, setContent] = useState({
    hero: {
      title: '云帐天下智能财务系统',
      subtitle: '专业的财务管理解决方案',
      description: '为企业提供全面的财务管理服务，助力企业数字化转型',
      backgroundImage: '',
      logo: '',
      slides: [
        {
          title: '智能财务管理',
          subtitle: '让财务工作更简单高效',
          description: '通过AI技术，自动化处理日常财务工作，提升工作效率',
          image: ''
        },
        {
          title: '数据驱动决策',
          subtitle: '基于数据的智能分析',
          description: '实时财务数据分析，为企业决策提供有力支持',
          image: ''
        },
        {
          title: '安全可靠',
          subtitle: '企业级安全保障',
          description: '多重安全防护，确保您的财务数据安全无忧',
          image: ''
        }
      ]
    },
    about: {
      title: '关于云帐天下',
      description: '云帐天下是一家专注于智能财务管理的科技公司，致力于为企业提供专业的财务解决方案。',
      features: [
        {
          title: '智能记账',
          description: 'AI自动识别票据，智能分类记账',
          icon: 'faRobot'
        },
        {
          title: '财务分析',
          description: '多维度财务数据分析，洞察企业经营状况',
          icon: 'faChartLine'
        },
        {
          title: '税务管理',
          description: '智能税务计算，合规申报无忧',
          icon: 'faFileInvoice'
        },
        {
          title: '报表生成',
          description: '一键生成各类财务报表，支持多种格式导出',
          icon: 'faTable'
        }
      ],
      teamImage: '',
      companyImage: ''
    },
    contact: {
      title: '联系我们',
      description: '我们随时为您提供专业的服务和支持',
      phone: '400-123-4567',
      email: 'contact@yunzhang.com',
      address: '北京市朝阳区xxx大厦xxx室',
      workingHours: '周一至周五 9:00-18:00',
      mapImage: ''
    },
    footer: {
      companyName: '云帐天下科技有限公司',
      copyright: '© 2024 云帐天下. 保留所有权利.',
      links: [
        { name: '隐私政策', url: '/privacy' },
        { name: '服务条款', url: '/terms' },
        { name: '帮助中心', url: '/help' }
      ],
      socialLinks: [
        { name: '微信', url: '#', icon: 'faWeixin' },
        { name: '微博', url: '#', icon: 'faWeibo' }
      ]
    }
  });

  // 获取网站内容
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminAPI.getWebsiteContent();
      if (response.data.status === 'success') {
        setContent(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (err) {
      setError(err.message || '获取内容失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存内容
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await adminAPI.updateWebsiteContent(content);
      if (response.data.status === 'success') {
        setSuccess('内容保存成功');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || '保存内容失败');
    } finally {
      setSaving(false);
    }
  };

  // 上传图片
  const handleImageUpload = async (file, section, field, index = null) => {
    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await adminAPI.uploadImage(formData);
      if (response.data.status === 'success') {
        const imageUrl = response.data.data.url;
        
        if (index !== null) {
          // 更新数组中的图片
          setContent(prev => ({
            ...prev,
            [section]: {
              ...prev[section],
              [field]: prev[section][field].map((item, i) => 
                i === index ? { ...item, image: imageUrl } : item
              )
            }
          }));
        } else {
          // 更新单个图片
          setContent(prev => ({
            ...prev,
            [section]: {
              ...prev[section],
              [field]: imageUrl
            }
          }));
        }
        
        setSuccess('图片上传成功');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || '图片上传失败');
    } finally {
      setUploadingImage(false);
    }
  };

  // 更新内容
  const updateContent = (section, field, value, index = null) => {
    if (index !== null) {
      setContent(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: prev[section][field].map((item, i) => 
            i === index ? { ...item, ...value } : item
          )
        }
      }));
    } else {
      setContent(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  // 渲染首页内容管理
  const renderHeroContent = () => (
    <div className="space-y-6">
      {/* 品牌信息 */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">品牌信息</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              主标题
            </label>
            <input
              type="text"
              value={content.hero.title}
              onChange={(e) => updateContent('hero', 'title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              副标题
            </label>
            <input
              type="text"
              value={content.hero.subtitle}
              onChange={(e) => updateContent('hero', 'subtitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            描述
          </label>
          <textarea
            value={content.hero.description}
            onChange={(e) => updateContent('hero', 'description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo
            </label>
            <div className="flex items-center space-x-4">
              {content.hero.logo && (
                <img src={content.hero.logo} alt="Logo" className="h-16 w-16 object-contain" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload(file, 'hero', 'logo');
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              背景图片
            </label>
            <div className="flex items-center space-x-4">
              {content.hero.backgroundImage && (
                <img src={content.hero.backgroundImage} alt="背景" className="h-16 w-24 object-cover rounded" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload(file, 'hero', 'backgroundImage');
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* 轮播图内容 */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">轮播图内容</h3>
        {content.hero.slides.map((slide, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="text-md font-medium text-gray-800 mb-3">幻灯片 {index + 1}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  标题
                </label>
                <input
                  type="text"
                  value={slide.title}
                  onChange={(e) => updateContent('hero', 'slides', { title: e.target.value }, index)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  副标题
                </label>
                <input
                  type="text"
                  value={slide.subtitle}
                  onChange={(e) => updateContent('hero', 'slides', { subtitle: e.target.value }, index)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                描述
              </label>
              <textarea
                value={slide.description}
                onChange={(e) => updateContent('hero', 'slides', { description: e.target.value }, index)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                图片
              </label>
              <div className="flex items-center space-x-4">
                {slide.image && (
                  <img src={slide.image} alt={`幻灯片 ${index + 1}`} className="h-16 w-24 object-cover rounded" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(file, 'hero', 'slides', index);
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // 渲染关于我们内容管理
  const renderAboutContent = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">关于我们</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              标题
            </label>
            <input
              type="text"
              value={content.about.title}
              onChange={(e) => updateContent('about', 'title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              描述
            </label>
            <textarea
              value={content.about.description}
              onChange={(e) => updateContent('about', 'description', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                团队图片
              </label>
              <div className="flex items-center space-x-4">
                {content.about.teamImage && (
                  <img src={content.about.teamImage} alt="团队" className="h-16 w-24 object-cover rounded" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(file, 'about', 'teamImage');
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                公司图片
              </label>
              <div className="flex items-center space-x-4">
                {content.about.companyImage && (
                  <img src={content.about.companyImage} alt="公司" className="h-16 w-24 object-cover rounded" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(file, 'about', 'companyImage');
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // 渲染联系我们内容管理
  const renderContactContent = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">联系信息</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              标题
            </label>
            <input
              type="text"
              value={content.contact.title}
              onChange={(e) => updateContent('contact', 'title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              联系电话
            </label>
            <input
              type="text"
              value={content.contact.phone}
              onChange={(e) => updateContent('contact', 'phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              邮箱地址
            </label>
            <input
              type="email"
              value={content.contact.email}
              onChange={(e) => updateContent('contact', 'email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              工作时间
            </label>
            <input
              type="text"
              value={content.contact.workingHours}
              onChange={(e) => updateContent('contact', 'workingHours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            描述
          </label>
          <textarea
            value={content.contact.description}
            onChange={(e) => updateContent('contact', 'description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            地址
          </label>
          <textarea
            value={content.contact.address}
            onChange={(e) => updateContent('contact', 'address', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">内容管理</h1>
            <p className="mt-1 text-sm text-gray-500">
              管理网站的图片、文字内容和页面信息
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={saving || uploadingImage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <FontAwesomeIcon icon={faSave} className="mr-2" />
              )}
              {saving ? '保存中...' : '保存更改'}
            </button>
          </div>
        </div>

        {/* 消息提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon icon={faCheck} className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        {uploadingImage && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 text-blue-400 animate-spin" />
              <div className="ml-3">
                <p className="text-sm text-blue-700">正在上传图片...</p>
              </div>
            </div>
          </div>
        )}

        {/* 标签页导航 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'hero', name: '首页内容', icon: faGlobe },
              { id: 'about', name: '关于我们', icon: faUsers },
              { id: 'contact', name: '联系我们', icon: faEnvelope }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FontAwesomeIcon 
                  icon={tab.icon} 
                  className={`mr-2 h-4 w-4 ${
                    activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} 
                />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* 内容区域 */}
        <div>
          {activeTab === 'hero' && renderHeroContent()}
          {activeTab === 'about' && renderAboutContent()}
          {activeTab === 'contact' && renderContactContent()}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ContentManagement;